import type { TFunction } from 'i18next';
import type { AppLocale } from '../i18n';
import { toPersianDigits } from './persianUtils';

const DURATION_SEP = ' · ';

function localizeNumericLabel(label: string, locale: AppLocale): string {
  return locale === 'fa' ? toPersianDigits(label) : label;
}

function labelDays(count: number, t: TFunction<'subscription'>, locale: AppLocale): string {
  return localizeNumericLabel(t('planDurationDays', { count }), locale);
}

function labelMonths(count: number, t: TFunction<'subscription'>, locale: AppLocale): string {
  return localizeNumericLabel(t('planDurationMonths', { count }), locale);
}

function labelYears(count: number, t: TFunction<'subscription'>, locale: AppLocale): string {
  return localizeNumericLabel(t('planDurationYears', { count }), locale);
}

export function formatPlanDurationLabel(
  days: number,
  t: TFunction<'subscription'>,
  locale: AppLocale
): string {
  const d = Math.round(Number(days));
  if (!Number.isFinite(d) || d <= 0) {
    return '—';
  }

  if (d === 30) return t('planDurationPreset1Month');
  if (d === 60) return t('planDurationPreset2Months');
  if (d === 90) return t('planDurationPreset3Months');
  if (d === 180) return t('planDurationPreset6Months');
  if (d === 365) return t('planDurationPreset1Year');

  if (d < 30) {
    return labelDays(d, t, locale);
  }

  if (d < 365) {
    const months = Math.floor(d / 30);
    const remainingDays = d % 30;

    if (remainingDays === 0) {
      return labelMonths(months, t, locale);
    }

    return [labelMonths(months, t, locale), labelDays(remainingDays, t, locale)].join(DURATION_SEP);
  }

  const years = Math.floor(d / 365);
  const remainingDays = d % 365;

  if (remainingDays === 0) {
    return labelYears(years, t, locale);
  }

  const months = Math.floor(remainingDays / 30);
  const extraDays = remainingDays % 30;
  const parts: string[] = [labelYears(years, t, locale)];

  if (months > 0) {
    parts.push(labelMonths(months, t, locale));
  }
  if (extraDays > 0) {
    parts.push(labelDays(extraDays, t, locale));
  }

  return parts.join(DURATION_SEP);
}

export function formatPlanPriceLabel(
  price: number,
  locale: AppLocale,
  currency = 'EUR'
): string {
  try {
    const formatted = new Intl.NumberFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
    return locale === 'fa' ? toPersianDigits(formatted) : formatted;
  } catch {
    const fallback = `€${price.toFixed(2)}`;
    return locale === 'fa' ? toPersianDigits(fallback) : fallback;
  }
}
